import { config, debug, george, NetbankingAPI, pr, theme } from "aaa";
import { ui } from "components/util/uiHelpers";
import { compact, flatMap, range, sortBy, uniq } from "lodash";
import { observer } from "mobx-react";
import { themeColors } from "muiTheme";
import { NetbankingAPIClient } from "network/APIClient";
import * as React from "react";
import authStore from "stores/AuthStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { landingPageStore } from "stores/LandingPageStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import { amountToCurrencyString } from "types/amount";
import { IGetCreditCardUpgradeResponse } from "types/card";
import { ILandingPageCondition, ILandingPageType } from "types/landingPage";
import { IPluginDetails, IPluginImage, IPluginText } from "types/plugins";
import { IProductDetails, IProductImage, IProductText } from "types/products";
import { georgeWebHeaderStore } from "./GeorgeWebHeader";
import { InfoBox } from "./InfoBox";
import { NativeModal } from "./NativeModal";
import { NativeRaisedButton } from "./NativeRaisedButton";

const useMockData = false;

const DIALOG_HEIGHT = 200;

type IComparisonDetails = {
    [key: string]: IPluginDetails | IProductDetails
};

type IStore = {
    isLoading?: boolean;
    conditions?: ILandingPageCondition[];
    upgradeDetails?: IGetCreditCardUpgradeResponse;
};

const getListItemSymbol = (symbol?: string) => {
    switch (symbol?.toLowerCase()) {
        case "none":
            return null;
        case "circle":
            return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            backgroundColor: ui.isDarkMode ? themeColors.d_grey_200 : themeColors.grey_300,
                            borderRadius: 10,
                            flexShrink: 0
                        }}
                    />
                </div>
            );
        case "checkmark":
        default:
            return <pr.IconCheckmarkCircle style={{ color: ui.isDarkMode ? themeColors.d_grey_200 : themeColors.grey_300, flexShrink: 0 }} />;
    }
};

export const ListItem = ({ children, symbol }: { children: React.ReactNode, symbol?: string }) => {
    const listItemSymbol = getListItemSymbol(symbol);

    return (
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: theme.MARGIN_SMALL }}>
            {listItemSymbol}
            <div style={{ marginLeft: listItemSymbol ? theme.MARGIN_SMALL : undefined, lineHeight: "24px" }}>
                {children}
            </div>
        </div>
    );
};

export const ListItemWithJsxSymbol = ({ children, symbol }: { children: React.ReactNode, symbol: JSX.Element | null }) => {
    return (
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: theme.MARGIN_SMALL }}>
            {symbol}
            <div style={{ marginLeft: symbol ? theme.MARGIN_SMALL : undefined, lineHeight: "24px" }}>
                {children}
            </div>
        </div>
    );
};

const ConditionTable = observer(({ conditions, description }: { conditions: ILandingPageCondition[], description?: string }) => (
    <div style={{ maxWidth: 500, margin: "0 auto" }}>
        {description && (
            <>
                <pr.P_16_TITLE style={{ marginBottom: conditions.length > 0 ? undefined : theme.MARGIN_LARGE }}>{description}</pr.P_16_TITLE>
                {conditions.length > 0 && <pr.LightSeparatorLine spacing={theme.MARGIN_SMALL} />}
            </>
        )}
        {conditions.map((condition, index) => (
            <React.Fragment key={`CONDITION_${index}`}>
                <george.ResponsiveFormElement
                    label={condition.label}
                    labelStyle={ui.windowWidth >= 600 ? { width: "40%" } : undefined}
                    elementStyle={ui.windowWidth >= 600 ? { width: "60%" } : undefined}
                    style={{ marginBottom: theme.MARGIN_SMALL, marginTop: theme.MARGIN_SMALL }}
                    align="right"
                >
                    <pr.P_16_CONTENT>{condition.value}</pr.P_16_CONTENT>
                </george.ResponsiveFormElement>
                {index + 1 !== conditions.length &&
                    <pr.LightSeparatorLine />
                }
            </React.Fragment>
        ))}
    </div>
));

const ProductCard = ({
    title,
    text,
    imageUrl,
    buttonText,
    handoverUrl,
    internalUrl,
    trackingEvent,
    accessToken,
}: {
    title?: string;
    text?: string;
    imageUrl?: string;
    buttonText?: string;
    handoverUrl?: string;
    internalUrl?: string;
    trackingEvent?: string;
    accessToken?: string;
}) => {
    let url = "";

    if (!internalUrl && handoverUrl && accessToken) {
        url = `${handoverUrl}${handoverUrl?.includes("?") ? "&" : "?"}channel=${externalInterfaceStore.channel ?? ""}#access_token=${accessToken}`;
    } else if (!handoverUrl && internalUrl) {
        url = `${internalUrl}${internalUrl?.includes("?") ? "&" : "?"}channel=${externalInterfaceStore.channel ?? ""}#access_token=${authStore.accessToken}`;
    }

    const handleClickButton = async () => {
        if (useMockData) {
            alert(`opens: ${handoverUrl || internalUrl}\ntracks: ${trackingEvent}`);
        } else {
            if (trackingEvent) {
                await trackingStore.trackEvent({ eventName: trackingEvent });
            }
        }
    };

    return (
        <div className="theme-paper-elevated" style={{ display: "flex", flexDirection: "column", width: 260 }} >
            {!useMockData && <img src={imageUrl} style={{ borderRadius: "4px 4px 0 0" }} />}
            {useMockData && (
                <div
                    style={{
                        boxSizing: "border-box",
                        height: 100,
                        width: "100%",
                        padding: theme.MARGIN,
                        background: "linear-gradient(45deg, #CCCCCC 50%, #888888 100%)",
                        wordBreak: "break-all"
                    }}
                >
                    {imageUrl}
                </div>
            )}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    justifyContent: "space-between",
                    padding: 16,
                    overflow: useMockData ? "hidden" : undefined,
                }}
            >
                <div>
                    <pr.P_16_TITLE style={{ fontWeight: 700, wordBreak: useMockData ? "break-all" : undefined }}>
                        {title}
                    </pr.P_16_TITLE>
                    <pr.P_16_CONTENT
                        style={{ marginTop: theme.MARGIN_SMALL, wordBreak: useMockData ? "break-all" : undefined }}
                        dangerouslySetInnerHTML={{ __html: text }}
                    />
                </div>
                {(url || useMockData) && (
                    <div style={{ marginTop: theme.MARGIN }}>
                        <a
                            href={externalInterfaceStore.isGeorgeApp
                                ? `georgeapp://at/open_external_link?url=${encodeURIComponent(url)}`
                                : url
                            }
                            target={internalUrl ? "_self" : "_blank"}
                        >
                            <NativeRaisedButton
                                className="theme-action-button-background-color"
                                style={{
                                    boxShadow: "none",
                                    borderRadius: theme.BORDER_RADIUS,
                                }}
                                primary
                                labelStyle={{
                                    color: "white",
                                    fontSize: 16,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    wordBreak: useMockData ? "break-all" : undefined,
                                }}
                                buttonStyle={useMockData ? { height: "unset" } : undefined}
                                label={buttonText}
                                onClick={handleClickButton}
                            />
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};

const headerCellStyle = {
    color: themeColors.grey_400,
    fontWeight: 600,
    padding: 16,
    fontSize: 12,
    lineHeight: "16px"
};

const cellStyle = {
    borderTop: "1px solid #e4eaf0",
    borderBottom: "1px solid #e4eaf0",
    padding: "16px 8px",
    fontSize: 12,
    lineHeight: "16px",
};

const InformationModal = ({ title, text, onClose }: { title: string, text: string, onClose?: () => void }) => {
    const [linksInitialized, setLinksInitialized] = React.useState(false);

    const modalTextRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!linksInitialized && modalTextRef.current) {
            const links = modalTextRef.current.getElementsByTagName("a");

            if (links.length > 0) {
                Array.from(links).forEach(link => {
                    link.href = externalInterfaceStore.isGeorgeApp ?
                        `georgeapp://at/open_external_link?url=${encodeURIComponent(link.href)}` :
                        link.href;
                });

                setLinksInitialized(true);
            }
        }
    });

    return (
        <NativeModal
            open={!!text}
            paperStyle={{ borderRadius: theme.BORDER_RADIUS, minHeight: DIALOG_HEIGHT }}
            bodyStyle={{ padding: 0, display: "flex", borderTop: "none", borderBottom: "none", minHeight: DIALOG_HEIGHT }}
            aria-label={ui.__("email.addNew.title")}
        >
            {text && (
                <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    <div style={{ flexGrow: 1 }}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                position: "relative",
                                backgroundColor: ui.isDarkMode ? "#22222c" : themeColors.grey_200,
                                padding: "20px 60px"
                            }}
                        >
                            <pr.P_24_TITLE style={{ fontWeight: "bold" }}>
                                {title}
                            </pr.P_24_TITLE>
                            {/* <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={handleCloseModal}
                                        style={{ position: "absolute", right: 20, top: 20, cursor: "pointer" }}
                                    >
                                        <pr.IconClose style={{ display: "block", color: themeColors.blue_300, }} />
                                    </div> */}
                        </div>
                        <div ref={modalTextRef}>
                            <pr.P_16_CONTENT style={{ padding: theme.MARGIN }} dangerouslySetInnerHTML={{ __html: text }} />
                        </div>
                    </div>
                    <div style={{ padding: theme.MARGIN }}>
                        <pr.A11yRefocus>
                            <NativeRaisedButton
                                className="theme-action-button-background-color"
                                style={{
                                    height: 40,
                                    boxShadow: "none",
                                    borderRadius: theme.BORDER_RADIUS,
                                }}
                                fullWidth
                                primary
                                labelStyle={{ color: "white", fontSize: 16, fontWeight: 600, textTransform: "none" }}
                                label={ui.__("common.close")}
                                onClick={onClose}
                            />
                        </pr.A11yRefocus>
                    </div>
                </div>
            )}
        </NativeModal>
    );
};

const ComparisonTable = observer(({
    store,
    details,
    comparisonCodes,
    comparisonDetails,
    style
}: {
    store?: IStore,
    details: IPluginDetails | IProductDetails,
    comparisonCodes?: string[],
    comparisonDetails?: IComparisonDetails,
    style?: React.CSSProperties
}) => {
    const [modalText, setModalText] = React.useState("");
    const [modalTitle, setModalTitle] = React.useState("");

    const supportForex = store?.upgradeDetails?.offer?.supportsLoyaltyForex;

    if (comparisonCodes && comparisonDetails) {
        let rows: { name: string, label: string, modalText: string }[] = compact([
            {
                name: "turnover",
                label: ui.__("landingPage.comparisonTable.turnover"),
                modalText: getText(details, "APP_FEATURE_TURNOVER_MODAL")
            },
            supportForex ? {
                name: "forex",
                label: ui.__("landingPage.comparisonTable.forex"),
                modalText: getText(details, "APP_FEATURE_FOREX_MODAL")
            } : undefined,
            {
                name: "splitnow",
                label: ui.__("landingPage.comparisonTable.splitnow"),
                modalText: getText(details, "APP_FEATURE_SPLITNOW_MODAL")
            },
            {
                name: "insurance",
                label: ui.__("landingPage.comparisonTable.insurance"),
                modalText: getText(details, "APP_FEATURE_INSURANCE_MODAL")
            },
            {
                name: "airport",
                label: ui.__("landingPage.comparisonTable.airport"),
                modalText: getText(details, "APP_FEATURE_AIRPORT_MODAL")
            },
            {
                name: "prioritypass",
                label: ui.__("landingPage.comparisonTable.prioritypass"),
                modalText: getText(details, "APP_FEATURE_PRIORITY_PASS_MODAL")
            },
            {
                name: "feeFirstYear",
                label: ui.__("landingPage.comparisonTable.feeFirstYear"),
                modalText: getText(details, "APP_FEATURE_FEE_FIRST_YEAR_MODAL")
            },
            {
                name: "fee",
                label: ui.__("landingPage.comparisonTable.fee"),
                modalText: getText(details, "APP_FEATURE_FEE_MODAL")
            },
        ]);

        const getFee = (comparisonCode: string) => {
            if (comparisonCode === store?.upgradeDetails?.card?.productCode) {
                return amountToCurrencyString(store?.upgradeDetails.card.fee);
            } else if (store?.upgradeDetails?.offer?.channelProduct && comparisonCode === `${store?.upgradeDetails?.offer?.channelProduct}_UPGRADE`) {
                return amountToCurrencyString(store?.upgradeDetails.offer.fee);
            }

            return getText(comparisonDetails[comparisonCode], "APP_FEATURE_FEE", true);
        };

        const getFeeFirstYear = (comparisonCode: string) => {
            if (comparisonCode === store?.upgradeDetails?.card?.productCode) {
                return amountToCurrencyString(store?.upgradeDetails.card.fee);
            } else if (store?.upgradeDetails?.offer?.channelProduct && comparisonCode === `${store?.upgradeDetails?.offer?.channelProduct}_UPGRADE`) {
                return amountToCurrencyString(store?.upgradeDetails.offer.feeFirstYear);
            }

            return getText(comparisonDetails[comparisonCode], "APP_FEATURE_FEE_FIRST_YEAR", true);
        };

        const columns = flatMap((useMockData ? mockComparisonProducts : comparisonCodes), comparisonCode => (
            !!comparisonDetails[comparisonCode]
                ? {
                    name: getText(comparisonDetails[comparisonCode], "NAME", true),
                    turnover: getText(comparisonDetails[comparisonCode], "APP_FEATURE_TURNOVER", true),
                    forex: getText(comparisonDetails[comparisonCode], "APP_FEATURE_FOREX", true),
                    splitnow: getText(comparisonDetails[comparisonCode], "APP_FEATURE_SPLITNOW", true),
                    insurance: getText(comparisonDetails[comparisonCode], "APP_FEATURE_INSURANCE", true),
                    airport: getText(comparisonDetails[comparisonCode], "APP_FEATURE_AIRPORT", true),
                    prioritypass: getText(comparisonDetails[comparisonCode], "APP_FEATURE_PRIORITY_PASS", true),
                    feeFirstYear: getFeeFirstYear(comparisonCode),
                    fee: getFee(comparisonCode),
                }
                : []
        ));

        // Throw away all rows that have no valid column text
        rows = rows.filter(row => {
            const hasAnyColumn = columns.find(column => !!column[row.name]) !== undefined;
            return hasAnyColumn;
        });

        const handleClickRow = (title: string, text: string) => {
            setModalTitle(title);
            setModalText(text);
        };

        const handleCloseModal = () => {
            setModalTitle("");
            setModalText("");
        };

        return (
            <div style={style}>
                <div style={{ width: "100%", overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", color: ui.isDarkMode ? "white" : themeColors.blue_400 }}>
                        <thead>
                            <tr>
                                <th style={{ ...headerCellStyle, textAlign: "left" }}>
                                    {ui.__("landingPage.comparisonTable.feature")}
                                </th>
                                {columns.map(column => (
                                    <th
                                        key={column.name}
                                        style={headerCellStyle}
                                        scope="col"
                                    >
                                        {column.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={row.name} style={{ cursor: row.modalText ? "pointer" : undefined }} onClick={() => handleClickRow(row.label, row.modalText)}>
                                    <th
                                        style={{
                                            ...cellStyle,
                                            fontWeight: "normal",
                                            textAlign: "left",
                                            paddingLeft: 16
                                        }}
                                        scope="row"
                                        className={index % 2 === 1 ? "alternate" : undefined}
                                    >
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {row.label}
                                            {row.modalText && (
                                                <div style={{ marginLeft: 6, display: "flex", alignItems: "center" }}>
                                                    <pr.IconInfoCircleHollow style={{ width: 16, height: 16 }} />
                                                </div>
                                            )}
                                        </div>
                                    </th>
                                    {columns.map(column => (
                                        <td
                                            key={`${row.name}_${column.name}`}
                                            style={{
                                                ...cellStyle,
                                                textAlign: "center",
                                                fontWeight: 600
                                            }}
                                            className={index % 2 === 1 ? "alternate" : undefined}
                                        >
                                            {column[row.name]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {
                    modalText && modalTitle && (
                        <InformationModal title={modalTitle} text={modalText} onClose={handleCloseModal} />
                    )
                }
            </div>
        );
    }

    return null;
});

const Image = ({ url }: { url: string }) => (
    <div
        style={{
            boxSizing: "border-box",
            height: 240,
            background: useMockData ? "linear-gradient(45deg, #CCCCCC 50%, #888888 100%)" : `url(${url}) no-repeat`,
            backgroundPosition: "50% 50%",
            backgroundSize: "cover",
            marginTop: theme.MARGIN,
            marginBottom: theme.MARGIN,
            padding: useMockData ? theme.MARGIN : undefined,
        }}
    >
        {useMockData && url}
    </div>
);

const productLandingPageConfig = [
    "APP_INTRO_BLOCK",
    "APP_TEXT_BLOCK_TOP_{N}",
    "APP_LIST_{N}",
    "APP_TEXT_BLOCK_BOTTOM_{N}",
    "APP_EXAMPLE_{N}",
    "APP_CONDITIONS",
    "APP_DISCLAIMER",
    "APP_BUTTON",
];

const pluginLandingPageConfig = productLandingPageConfig;

const generateMockTexts = () => flatMap(productLandingPageConfig, (type, index) => {
    /*
        generateElement generates "count" Elements/Texts (replacing {N}) and if there is a Subelement/Subtext generate "itemCount" Subelement/Subtexts (replacing {X})

        Example 1: generate 2 APP_LIST_{N}_TITLE items
        Direct call of function:
            generateElement(2)("APP_LIST_{N}_TITLE")
        Results in 2 textitems for:
            APP_LIST_1_TITLE
            APP_LIST_2_TITLE

        Example 2: generate 3 APP_LIST_{N}_ITEM_{X}
        Direct call of function:
            generateElement(2, 3)("APP_LIST_{N}_ITEM_{X}")
        Results in 6 textitems for:
            APP_LIST_1_ITEM_1
            APP_LIST_1_ITEM_2
            APP_LIST_1_ITEM_3
            APP_LIST_2_ITEM_1
            APP_LIST_2_ITEM_2
            APP_LIST_2_ITEM_3

        Example 3: APP_LIST_{N}_TITLE and APP_LIST_{N}_ITEM_{X} occur together so we want to batch them
        If batches of elements are needed it makes sense to use it as generator Function like so:
        Calling:
            flatMap(["APP_LIST_{N}_TITLE", "APP_LIST_{N}_ITEM_{X}"], generateElement(2, 3));
        Will Result in textitems for:
            APP_LIST_1_TITLE
            APP_LIST_1_ITEM_1
            APP_LIST_1_ITEM_2
            APP_LIST_1_ITEM_3
            APP_LIST_2_TITLE
            APP_LIST_2_ITEM_1
            APP_LIST_2_ITEM_2
            APP_LIST_2_ITEM_3

        Using this batching strategy with elements from productLandingPageConfig which do not include an {N} placeholder
        but has other elements that can occur multiple times below it, should be generated with providing {X} as placeholder
        in order to avoid multiple generation of singular elements
        Example 4: generate APP_CONDITIONS block with 2 APP_CONDITIONS_INFO_{NUMBER} items
            flatMap(["APP_CONDITIONS_TITLE", "APP_CONDITIONS_INFO_{X}"], generateElement(1, 2))
        Results in textitems for:
            APP_CONDITIONS_TITLE
            APP_CONDITIONS_INFO_1
            APP_CONDITIONS_INFO_2
        but using:
            flatMap(["APP_CONDITIONS_TITLE", "APP_CONDITIONS_INFO_{N}"], generateElement(2, 1))
        would result in textitems for:
            APP_CONDITIONS_TITLE
            APP_CONDITIONS_TITLE
            APP_CONDITIONS_INFO_1
            APP_CONDITIONS_INFO_2
    */
    const generateElement = (count: number = 1, itemCount: number = 1) => (elementType: string) =>
        flatMap(range(1, count + 1), elementNumber =>
            flatMap(range(1, itemCount + 1), itemNumber => {
                const name = elementType.replace("{N}", String(elementNumber)).replace("{X}", String(itemNumber));

                return [{
                    type: name,
                    description: "",
                    id: index,
                    pluginId: 123,
                    textColor: "",
                    translations: [{
                        id: index,
                        language: "DE" as "DE",
                        plainText: name,
                        text: name
                    }, {
                        id: index,
                        language: "EN" as "EN",
                        plainText: name,
                        text: name
                    }]
                }];
            })
        );

    if (type === "APP_INTRO_BLOCK") {
        return flatMap(["APP_HEADER_TITLE", "APP_INTRO_BLOCK_TITLE", "APP_INTRO_BLOCK_SUBTITLE", "APP_INTRO_BLOCK"], generateElement());
    }

    if (type === "APP_TEXT_BLOCK_TOP_{N}" || type === "APP_TEXT_BLOCK_BOTTOM_{N}") {
        return flatMap([`${type}_TITLE`, type, `${type}_IMG`], generateElement(2))
            .concat(flatMap([
                `${type}_PRODUCTCARD_{X}_IMAGEURL`,
                `${type}_PRODUCTCARD_{X}_TITLE`,
                `${type}_PRODUCTCARD_{X}_TEXT`,
                `${type}_PRODUCTCARD_{X}_BUTTON`,
                // `${type}_PRODUCTCARD_{X}_HANDOVERURL`, // can be used instead of INTERNALURL (mutually exclusive)
                `${type}_PRODUCTCARD_{X}_INTERNALURL`, // using INTERNALURL here allows to see the buttons
                `${type}_PRODUCTCARD_{X}_TRACKINGEVENT`,
            ], generateElement(1, 2)));
    }

    if (type === "APP_LIST_{N}") {
        return flatMap([`${type}_IMG`, `${type}_TITLE`, `${type}_ITEM_{X}`, `${type}_ITEM_{X}_TITLE`, `${type}_ADDENDUM`], generateElement(1, 5));
    }

    if (type === "APP_EXAMPLE_{N}") {
        return flatMap([`${type}_TITLE`, `${type}_DESCRIPTION`, `${type}_INFO_TITLE`, `${type}_INFO_{X}`], generateElement(2, 2))
            .concat(flatMap([`${type}_ITEM_{X}_LABEL`], generateElement(2, 3)))
            .concat(flatMap([`${type}_ITEM_{X}_VALUE`], generateElement(2, 3)));
    }

    if (type === "APP_CONDITIONS") {
        return flatMap([`${type}_TITLE`, `${type}_DESCRIPTION`, `${type}_INFO_TITLE`, `${type}_INFO_{X}`], generateElement(1, 2))
            .concat(flatMap([`${type}_ITEM_{X}_LABEL`], generateElement(1, 3)))
            .concat(flatMap([`${type}_ITEM_{X}_VALUE`], generateElement(1, 3)));
    }

    if (type === "APP_DISCLAIMER") {
        return flatMap([`${type}`, `${type}_TITLE`], generateElement());
    }

    return generateElement()(type);
});

const mockImage = {
    id: 2520,
    pluginId: 604,
    uri: "/plugins/image_604_2520_s.png",
    name: "s.png",
    language: "EU"
};

const mockData: IProductDetails = {
    id: 604,
    externalId: "ACCIDENT_INSURANCE",
    keyVisual: mockImage,
    listImage: mockImage,
    xlImage: mockImage,
    detailsImage: mockImage,
    featured: true,
    position: 14,
    inUse: true,
    hidden: false,
    type: "PRODUCT",
    texts: generateMockTexts(),
    videos: [],
    categories: [],
    tags: [],
    images: [],
    ratings: [],
    comments: [],
    components: [],
    simage: mockImage,
    limage: mockImage,
    mimage: mockImage
};

const mockConditions: ILandingPageCondition[] = [{
    label: "Condition 1",
    value: "13,00 €"
}, {
    label: "Condition 2",
    value: "7,00 €"
}, {
    label: "Condition 2",
    value: "5%"
}];

const mockComparisonProducts = ["PRODUCT_1", "PRODUCT_2"];

const mockComparisonDetails: { [key: string]: IPluginDetails | IProductDetails } = mockComparisonProducts.reduce((accumulator, comparisonProduct) => ({
    ...accumulator,
    [comparisonProduct]: {
        ...mockData,
        externalId: comparisonProduct,
        texts: [
            "NAME",
            "APP_FEATURE_TURNOVER",
            "APP_FEATURE_FOREX",
            "APP_FEATURE_SPLITNOW",
            "APP_FEATURE_INSURANCE",
            "APP_FEATURE_AIRPORT",
            "APP_FEATURE_PRIORITY_PASS",
            "APP_FEATURE_FEE",
            "APP_FEATURE_FEE_FIRST_YEAR",
        ].map((name, index) => ({
            type: name,
            description: "",
            id: index,
            pluginId: 123,
            textColor: "",
            translations: [{
                id: index,
                language: "DE" as "DE",
                plainText: `${comparisonProduct}__${name}`,
                text: `${comparisonProduct}__${name}`
            }, {
                id: index,
                language: "EN" as "EN",
                plainText: `${comparisonProduct}__${name}`,
                text: `${comparisonProduct}__${name}`
            }]
        }))
    }
}), {});

const getConfig = (type: ILandingPageType) => {
    switch (type) {
        case "PRODUCT": return productLandingPageConfig;
        case "PLUGIN": return pluginLandingPageConfig;
        default: return null;
    }
};

const sanitize = (text: string) => {
    return text.replace(/↵|\n/g, "");
};

const getTranslation = (text: IProductText | IPluginText, plainTextOnly?: boolean) => {
    const translation = text.translations.find(translationItem => translationItem.language === authStore.locale.toUpperCase());

    if (translation) {
        if (
            text.type.includes("TITLE") ||
            text.type.includes("BUTTON") ||
            text.type.includes("IMG") ||
            (text.type.includes("APP_CONDITIONS") && (text.type.includes("ITEM") || text.type.includes("DESCRIPTION"))) ||
            (text.type.includes("APP_EXAMPLE") && (text.type.includes("ITEM") || text.type.includes("DESCRIPTION"))) ||
            (text.type.includes("APP_LIST") && text.type.includes("SYMBOL"))
        ) {
            return sanitize(translation.plainText);
        }

        return plainTextOnly ? sanitize(translation.plainText) : sanitize(translation.text);
    }

    return "";
};

const getText = (details: IProductDetails | IPluginDetails, type: string, plainTextOnly?: boolean) => {
    const texts = details.texts.find(textsItem => textsItem.type === type);

    return texts ? getTranslation(texts, plainTextOnly) : "";
};

const getImage = (imageName: string, images: IProductImage[] | IPluginImage[]) => {
    if (useMockData) {
        return imageName;
    }

    const imageUri = images.find(image => image.name === imageName)?.uri;

    return imageUri ? encodeURI(`${config.network.imageBaseUrl}${imageUri}`) : "";
};

const SeparatorLine = () => (
    <div style={{ marginTop: theme.MARGIN, marginBottom: theme.MARGIN }}>
        <pr.LightSeparatorLine />
    </div>
);

const titleStyle = { fontWeight: "bold", marginBottom: theme.MARGIN };

type IProps = {
    type: ILandingPageType;
    code: string | undefined;
    comparisonCodes?: string[];
    codeSuffix?: string;
    store?: IStore;
    conditionsComponent?: React.ReactNode;
    onClickButton?: () => void;
    useWebVersion?: boolean;
};

export const LandingPage = observer(({
    type,
    code,
    comparisonCodes,
    codeSuffix,
    store,
    conditionsComponent,
    onClickButton,
    useWebVersion
}: IProps) => {
    const [details, setDetails] = React.useState<IPluginDetails | IProductDetails | null>(useMockData ? mockData : null);
    const [accessToken, setAccessToken] = React.useState("");
    const [comparisonDetails, setComparisonDetails] = React.useState<{ [key: string]: IPluginDetails | IProductDetails } | undefined>(
        useMockData ? mockComparisonDetails : undefined
    );
    const [linksInitialized, setLinksInitialized] = React.useState(false);

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 800;

    // this only works if there is no document/george header title set in the container component
    React.useEffect(() => {
        if (details) {
            const landingPageTitle = getText(details, "APP_HEADER_TITLE");

            if (document.title !== landingPageTitle && landingPageTitle) {
                try {
                    document.title = landingPageTitle;

                    georgeWebHeaderStore.configure({
                        title: landingPageTitle,
                    });

                } catch (error) {
                    if (!config.DEBUG) {
                        pushRoute("/error");
                    }
                }
            }
        }
    }, [details]);

    React.useEffect(() => {
        const loadDetails = async () => {
            try {
                if (!useMockData) {
                    await landingPageStore.load(type);
                    if (code) {
                        const landingPageDetails = landingPageStore.getDetails(type, codeSuffix ? `${code}_${codeSuffix}` : code);
                        if (landingPageDetails) {
                            setDetails(landingPageDetails);
                        } else {
                            debug.log("### LandingPage could not find any product/plugin details in response");
                        }

                        if (comparisonCodes) {
                            const comparisonDetailsByCode = comparisonCodes?.reduce((accumulator, currentValue) => ({
                                ...accumulator,
                                [currentValue]: landingPageStore.getDetails(type, currentValue)
                            }), {});

                            setComparisonDetails(comparisonDetailsByCode);
                        }

                    } else {
                        debug.log("### LandingPage no code provided");
                    }
                }
            } catch (error) {
                debug.log("### LandingPage failed to load details", error);
                pushRoute("/error");
            }
        };

        loadDetails();
    }, [type, code, codeSuffix]);

    React.useEffect(() => {
        if (!linksInitialized) {
            const links = document.getElementsByTagName("a");

            if (links.length > 0) {
                Array.from(links).forEach(link => {
                    link.href = externalInterfaceStore.isGeorgeApp ?
                        `georgeapp://at/open_external_link?url=${encodeURIComponent(link.href)}` :
                        link.href;
                });

                setLinksInitialized(true);
            }
        }
    }, []);

    const hasProductCardWithHandoverUrl = details?.texts.some(textBlock =>
        new RegExp(`.*_PRODUCTCARD_([0-9]+_HANDOVERURL)`).test(textBlock.type)
    );

    React.useEffect(() => {
        const tokenExchange = async () => {
            const res: any = await NetbankingAPIClient.requestJSON(NetbankingAPI.tokenExchange("F01145TD"));
            setAccessToken(res.access_token);
        };

        if (hasProductCardWithHandoverUrl) {
            tokenExchange();
        }
    }, [hasProductCardWithHandoverUrl]);

    const landingPageConfig = getConfig(type);

    if ((store && store.isLoading) || landingPageStore.isLoading || !landingPageConfig || !details) {
        return <george.LoadingIndicator isLoading height={ui.windowHeight} />;
    }

    return (
        <george.LandingBackground useWebVersion={useWebVersion}>
            <div style={{ lineHeight: "24px" }}>
                {landingPageConfig.map(elementType => {
                    if (elementType === "APP_INTRO_BLOCK") {
                        const introBlockTitle = getText(details, "APP_INTRO_BLOCK_TITLE");
                        const introBlockSubtitle = getText(details, "APP_INTRO_BLOCK_SUBTITLE");
                        const introBlockMain = getText(details, "APP_INTRO_BLOCK");

                        return (
                            <React.Fragment key={elementType}>
                                <div style={{ padding: theme.MARGIN }}>
                                    {introBlockTitle &&
                                        <pr.LandingPageTitle style={{ textAlign: "center" }}>
                                            {introBlockTitle}
                                        </pr.LandingPageTitle>
                                    }
                                    {introBlockSubtitle &&
                                        <pr.LandingPageSubtitle style={{ textAlign: "center", marginBottom: theme.MARGIN }}>
                                            {introBlockSubtitle}
                                        </pr.LandingPageSubtitle>
                                    }
                                    {introBlockMain &&
                                        <pr.P_16_CONTENT
                                            style={{ textAlign: "center", marginTop: theme.MARGIN }}
                                            dangerouslySetInnerHTML={{ __html: introBlockMain }}
                                        />
                                    }
                                </div>
                            </React.Fragment>
                        );
                    } else if (elementType.startsWith("APP_TEXT_BLOCK")) {
                        const textBlockType = elementType.startsWith("APP_TEXT_BLOCK_TOP") ? "APP_TEXT_BLOCK_TOP" : "APP_TEXT_BLOCK_BOTTOM";

                        const textBlockCount = details.texts.filter(textBlock => new RegExp(`${textBlockType}_([0-9]+)$`).test(textBlock.type)).length;

                        return range(1, textBlockCount + 1).map(textBlockNumber => {
                            const numberedBlockType = `${textBlockType}_${textBlockNumber}`;

                            const textBlockTitle = getText(details, `${numberedBlockType}_TITLE`);
                            const textBlockMain = getText(details, `${numberedBlockType}`);
                            const textBlockImage = getText(details, `${numberedBlockType}_IMG`);

                            const productCardCount = uniq(
                                details.texts
                                    .filter(textBlock => new RegExp(`${numberedBlockType}_PRODUCTCARD_([0-9]+)`).test(textBlock.type))
                                    .map(textBlock => textBlock.type.split("_")[6])
                            ).length;

                            const productCardsTitle = getText(details, `${numberedBlockType}_PRODUCTCARDS_TITLE`);

                            const productCardComponents = range(1, productCardCount + 1).map(productCardNumber => {
                                const numberedProductCardType = `${numberedBlockType}_PRODUCTCARD_${productCardNumber}`;

                                const productCardTitle = getText(details, `${numberedProductCardType}_TITLE`);
                                const productCardText = getText(details, `${numberedProductCardType}_TEXT`);
                                const productCardImageUrl = getText(details, `${numberedProductCardType}_IMAGEURL`, true);
                                const productCardButtonText = getText(details, `${numberedProductCardType}_BUTTON`, true);
                                const productCardHandoverUrl = getText(details, `${numberedProductCardType}_HANDOVERURL`, true);
                                const productCardHInternalUrl = getText(details, `${numberedProductCardType}_INTERNALURL`, true);
                                const productCardTrackingEvent = getText(details, `${numberedProductCardType}_TRACKINGEVENT`, true);

                                return (
                                    <ProductCard
                                        key={numberedProductCardType}
                                        title={productCardTitle}
                                        text={productCardText}
                                        imageUrl={productCardImageUrl}
                                        buttonText={productCardButtonText}
                                        handoverUrl={productCardHandoverUrl}
                                        internalUrl={productCardHInternalUrl}
                                        trackingEvent={productCardTrackingEvent}
                                        accessToken={accessToken}
                                    />
                                );
                            });

                            return (
                                <React.Fragment key={`${textBlockType}_${textBlockNumber}`}>
                                    {textBlockImage ? <Image url={getImage(textBlockImage, details.images)} /> : <SeparatorLine />}
                                    <div style={{ padding: theme.MARGIN }}>
                                        {textBlockTitle &&
                                            <pr.P_18_TITLE style={titleStyle}>
                                                {textBlockTitle}
                                            </pr.P_18_TITLE>
                                        }
                                        {textBlockMain &&
                                            <pr.P_16_CONTENT dangerouslySetInnerHTML={{ __html: textBlockMain }} />
                                        }
                                        {productCardComponents.length > 0 && (
                                            <div style={{ marginTop: theme.MARGIN_LARGE }}>
                                                <pr.P_16_TITLE style={{ ...titleStyle, textAlign: "center" }}>
                                                    {productCardsTitle}
                                                </pr.P_16_TITLE>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        flexWrap: "wrap",
                                                        gap: 40,
                                                        marginTop: theme.MARGIN_XLARGE
                                                    }}
                                                >
                                                    {productCardComponents}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </React.Fragment>
                            );
                        });
                    } else if (elementType.startsWith("APP_LIST")) {
                        const comparisonTableTitle = getText(details, "APP_COMPARISON", true);

                        const listCount = uniq(
                            details.texts
                                .filter(textBlock => new RegExp(`APP_LIST_([0-9]+)`).test(textBlock.type))
                                .map(textBlock => textBlock.type.split("_")[2])
                        ).length;

                        return range(1, listCount + 1).map(listNumber => {
                            const numberedBlockType = `APP_LIST_${listNumber}`;

                            const listSymbol = getText(details, `${numberedBlockType}_SYMBOL`);
                            const listBlockTitle = getText(details, `${numberedBlockType}_TITLE`);
                            const listBlockTemplateTitle = getText(details, `${numberedBlockType}_TEMPLATE_TITLE`);
                            const listItems = sortBy(
                                details.texts.filter(listBlock => new RegExp(`APP_LIST_${listNumber}_ITEM_([0-9]+)$`).test(listBlock.type)),
                                listBlock => listBlock.type
                            );
                            const listBlockAddendum = getText(details, `${numberedBlockType}_ADDENDUM`);
                            const listBlockImage = getText(details, `${numberedBlockType}_IMG`);

                            return (
                                <React.Fragment key={numberedBlockType}>
                                    {listBlockImage ? <Image url={getImage(listBlockImage, details.images)} /> : <SeparatorLine />}
                                    {listNumber === 1 && comparisonCodes && comparisonDetails && (
                                        <>
                                            <div style={{ padding: theme.MARGIN }}>
                                                {listBlockTemplateTitle && (
                                                    <pr.LandingPageTitle style={{ textAlign: "center" }}>
                                                        {listBlockTemplateTitle.replace("{value}", amountToCurrencyString(store?.upgradeDetails?.offer?.fee))}
                                                    </pr.LandingPageTitle>
                                                )}
                                                {comparisonTableTitle &&
                                                    <pr.P_18_TITLE style={{ fontWeight: "bold", marginTop: theme.MARGIN_LARGE }}>
                                                        {comparisonTableTitle}
                                                    </pr.P_18_TITLE>
                                                }
                                                {!isMobile && (
                                                    <ComparisonTable
                                                        store={store}
                                                        details={details}
                                                        comparisonCodes={comparisonCodes}
                                                        comparisonDetails={comparisonDetails}
                                                        style={{ marginTop: theme.MARGIN }}
                                                    />
                                                )}
                                            </div>
                                            {isMobile && (
                                                <ComparisonTable
                                                    store={store}
                                                    details={details}
                                                    comparisonCodes={comparisonCodes}
                                                    comparisonDetails={comparisonDetails}
                                                    style={{ marginBottom: theme.MARGIN }}
                                                />
                                            )}
                                        </>
                                    )}
                                    {(listBlockTitle || listItems.length > 0 || listBlockAddendum) && (
                                        <div style={{ padding: theme.MARGIN }}>
                                            {listBlockTitle &&
                                                <pr.P_18_TITLE style={titleStyle}>
                                                    {listBlockTitle}
                                                </pr.P_18_TITLE>
                                            }
                                            {listItems.map((listItem, index) => {
                                                const listItemTitle = getText(details, `${listItem.type}_TITLE`);

                                                return (
                                                    <ListItem key={`${listItem.id}_${index}`} symbol={listSymbol}>
                                                        {listItemTitle &&
                                                            <pr.P_16_TITLE style={{ fontWeight: "bold" }} dangerouslySetInnerHTML={{ __html: listItemTitle }} />
                                                        }
                                                        <pr.P_16_CONTENT dangerouslySetInnerHTML={{ __html: getTranslation(listItem) }} />
                                                    </ListItem>
                                                );
                                            })}
                                            {listBlockAddendum &&
                                                <pr.P_16_CONTENT style={{ marginTop: theme.MARGIN }} dangerouslySetInnerHTML={{ __html: listBlockAddendum }} />
                                            }
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        });
                    } else if (elementType.startsWith("APP_EXAMPLE")) {
                        const exampleBlockCount = uniq(
                            details.texts
                                .filter(textBlock => new RegExp(`APP_EXAMPLE_([0-9]+)`).test(textBlock.type))
                                .map(textBlock => textBlock.type.split("_")[2])
                        ).length;

                        return range(1, exampleBlockCount + 1).map(listNumber => {
                            const numberedBlockType = `APP_EXAMPLE_${listNumber}`;

                            const exampleTitle = getText(details, `${numberedBlockType}_TITLE`);
                            const exampleTableDescription = getText(details, `${numberedBlockType}_DESCRIPTION`);
                            const exampleLabels = sortBy(
                                details.texts.filter(exampleItem => new RegExp(`${numberedBlockType}_ITEM_([0-9]+)_LABEL$`).test(exampleItem.type)),
                                exampleItem => exampleItem.type
                            );
                            const exampleValues = sortBy(
                                details.texts.filter(exampleItem => new RegExp(`${numberedBlockType}_ITEM_([0-9]+)_VALUE$`).test(exampleItem.type)),
                                exampleItem => exampleItem.type
                            );
                            const exampleInfoTitle = getText(details, `${numberedBlockType}_INFO_TITLE`);
                            const exampleInfoItems = sortBy(
                                details.texts.filter(examplesInfoItem => new RegExp(`${numberedBlockType}_INFO_([0-9]+)$`).test(examplesInfoItem.type)),
                                examplesInfoItem => examplesInfoItem.type
                            );

                            const exampleIds = uniq(
                                exampleLabels
                                    .concat(exampleValues)
                                    .map(exampleItem => Number(exampleItem.type.replace(`${numberedBlockType}_ITEM_`, "").replace("_VALUE", "").replace("_LABEL", "")))
                            );

                            const examples = exampleIds.map(exampleId => {
                                const exampleLabel = exampleLabels.find(label => label.type === `${numberedBlockType}_ITEM_${exampleId}_LABEL`);
                                const exampleValue = exampleValues.find(label => label.type === `${numberedBlockType}_ITEM_${exampleId}_VALUE`);

                                return ({
                                    label: exampleLabel ? getTranslation(exampleLabel) : "",
                                    value: exampleValue ? getTranslation(exampleValue) : "",
                                });
                            });

                            return (
                                <React.Fragment key={numberedBlockType}>
                                    <SeparatorLine />
                                    {exampleTitle &&
                                        <div style={{ padding: theme.MARGIN }}>
                                            <pr.P_16_TITLE style={{ textAlign: "center", fontWeight: "bold", marginBottom: theme.MARGIN }}>
                                                {exampleTitle}
                                            </pr.P_16_TITLE>
                                        </div>
                                    }
                                    <div style={{ paddingLeft: theme.MARGIN, paddingRight: theme.MARGIN }}>
                                        <ConditionTable conditions={examples} description={exampleTableDescription} />
                                    </div>
                                    {exampleInfoItems.length > 0 &&
                                        <>
                                            <div style={{ textAlign: "center", padding: theme.MARGIN }}>
                                                {exampleInfoTitle && (
                                                    <pr.P_16_TITLE style={{ fontWeight: "bold", marginBottom: theme.MARGIN }}>
                                                        {exampleInfoTitle}
                                                    </pr.P_16_TITLE>
                                                )}
                                                {exampleInfoItems.map((exampleInfoItem, index) => (
                                                    <pr.P_16_CONTENT
                                                        key={`${exampleInfoItem.id}_${index}`}
                                                        style={{ marginTop: index > 0 ? theme.MARGIN_SMALL : undefined }}
                                                        dangerouslySetInnerHTML={{ __html: getTranslation(exampleInfoItem) }}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    }
                                </React.Fragment>
                            );
                        });
                    } else if (elementType.startsWith("APP_CONDITIONS")) {
                        const conditionsTitle = getText(details, `APP_CONDITIONS_TITLE`);
                        const conditionsTableDescription = getText(details, `APP_CONDITIONS_DESCRIPTION`);
                        const conditionLabels = sortBy(
                            details.texts.filter(conditionItem => new RegExp(`APP_CONDITIONS_ITEM_([0-9]+)_LABEL$`).test(conditionItem.type)),
                            conditionItem => conditionItem.type
                        );
                        const conditionValues = sortBy(
                            details.texts.filter(conditionItem => new RegExp(`APP_CONDITIONS_ITEM_([0-9]+)_VALUE$`).test(conditionItem.type)),
                            conditionItem => conditionItem.type
                        );
                        const conditionsInfoTitle = getText(details, `APP_CONDITIONS_INFO_TITLE`);
                        const conditionsInfoItems = sortBy(
                            details.texts.filter(conditionsInfoItem => new RegExp(`APP_CONDITIONS_INFO_([0-9]+)$`).test(conditionsInfoItem.type)),
                            conditionsInfoItem => conditionsInfoItem.type
                        );

                        const conditionIds = uniq(
                            conditionLabels
                                .concat(conditionValues)
                                .map(conditionItem => Number(conditionItem.type.replace(`APP_CONDITIONS_ITEM_`, "").replace("_VALUE", "").replace("_LABEL", "")))
                        );

                        const backOfficeConditions = conditionIds.map(conditionId => {
                            const conditionLabel = conditionLabels.find(label => label.type === `APP_CONDITIONS_ITEM_${conditionId}_LABEL`);
                            const conditionValue = conditionValues.find(label => label.type === `APP_CONDITIONS_ITEM_${conditionId}_VALUE`);

                            return ({
                                label: conditionLabel ? getTranslation(conditionLabel) : "",
                                value: conditionValue ? getTranslation(conditionValue) : "",
                            });
                        });

                        const showConditions = (useMockData || conditionsComponent || (store && store.conditions) || backOfficeConditions.length > 0);

                        const conditions = (store?.conditions ?? []).concat(backOfficeConditions);

                        if (!showConditions) {
                            return null;
                        }

                        return (
                            <React.Fragment key="APP_CONDITIONS">
                                <SeparatorLine />
                                {conditionsTitle &&
                                    <div style={{ padding: theme.MARGIN }}>
                                        <pr.P_16_TITLE style={{ textAlign: "center", fontWeight: "bold", marginBottom: theme.MARGIN }}>
                                            {conditionsTitle}
                                        </pr.P_16_TITLE>
                                    </div>
                                }
                                <div style={{ paddingLeft: theme.MARGIN, paddingRight: theme.MARGIN }}>
                                    {conditionsComponent || <ConditionTable conditions={conditions} description={conditionsTableDescription} />}
                                </div>
                                {conditionsInfoItems.length > 0 &&
                                    <>
                                        <SeparatorLine />
                                        <div style={{ textAlign: "center", padding: theme.MARGIN }}>
                                            {conditionsInfoTitle && (
                                                <pr.P_14_TITLE style={{ fontWeight: "bold", marginBottom: theme.MARGIN }}>
                                                    {conditionsInfoTitle}
                                                </pr.P_14_TITLE>
                                            )}
                                            {conditionsInfoItems.map((conditionsInfoItem, index) => (
                                                <pr.P_14_CONTENT
                                                    key={`${conditionsInfoItem.id}_${index}`}
                                                    style={{ marginTop: index > 0 ? theme.MARGIN_SMALL : undefined }}
                                                >
                                                    {getTranslation(conditionsInfoItem)}
                                                </pr.P_14_CONTENT>
                                            ))}
                                        </div>
                                    </>
                                }
                            </React.Fragment>
                        );
                    } else if (elementType.startsWith("APP_DISCLAIMER")) {
                        const disclaimerTitle = getText(details, `APP_DISCLAIMER_TITLE`);
                        const disclaimerMain = getText(details, `APP_DISCLAIMER`);

                        if (!disclaimerMain) {
                            return null;
                        }

                        return (
                            <React.Fragment key="APP_DISCLAIMER">
                                <SeparatorLine />
                                <div style={{ padding: theme.MARGIN }}>
                                    <InfoBox
                                        title={disclaimerTitle}
                                        content={<pr.P_14_CONTENT dangerouslySetInnerHTML={{ __html: disclaimerMain }} />}
                                    />
                                </div>
                            </React.Fragment>
                        );
                    } else if (elementType === "APP_BUTTON") {
                        const text = getText(details, elementType);

                        return text && (
                            <div key="APP_BUTTON" style={{ padding: theme.MARGIN, textAlign: "center" }}>
                                <NativeRaisedButton
                                    className="theme-action-button-background-color"
                                    style={{
                                        height: 60,
                                        boxShadow: "none",
                                        borderRadius: theme.BORDER_RADIUS,
                                    }}
                                    fullWidth={externalInterfaceStore.isGeorgeApp}
                                    primary
                                    labelStyle={{ color: "white", fontSize: 16, fontWeight: 600, textTransform: "none" }}
                                    label={text}
                                    onClick={onClickButton}
                                />
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
        </george.LandingBackground>
    );
});



// WEBPACK FOOTER //
// ./src/components/common/LandingPage.tsx